import type { KittingCartSlot } from "./kittingCartModel";
import type { KittingPickLine } from "./kittingPickModel";

/** Integer scale for `labor.device_scan.quantity` weights (session end divides net seconds by the sum). */
export const KITTING_PICK_LABOR_ALLOCATION_UNIT_SCALE = 10_000;

export type KittingPickLaborAllocationTarget =
  | { kind: "build_order"; buildOrderId: number }
  | { kind: "batch"; batchId: number }
  | { kind: "transfer_order"; transferOrderId: number };

export interface KittingPickLaborJobMetrics {
  locationCount: number;
  skuCount: number;
  qtyDueSum: number;
}

export interface KittingPickLaborAllocationRow {
  slotKey: string;
  quantity: number;
  metrics: KittingPickLaborJobMetrics;
  target: KittingPickLaborAllocationTarget;
}

export function kittingPickLaborDeviceSerial(slotKey: string): string {
  return `KITTING_PICK|${slotKey.replace(/\|/g, "_")}`;
}

/** Distinct storage stops, SKUs, and due quantities for one cart slot. */
export function kittingPickLaborJobMetrics(
  pickLines: readonly KittingPickLine[],
  slotId: string,
): KittingPickLaborJobMetrics {
  const locations = new Set<string>();
  const qtyBySku = new Map<string, number>();
  for (const line of pickLines) {
    if (line.destinationSlotId !== slotId) continue;
    locations.add(line.storageLocation);
    const previous = qtyBySku.get(line.sku) ?? 0;
    qtyBySku.set(line.sku, Math.max(previous, line.qtyDue));
  }
  let qtyDueSum = 0;
  for (const qty of qtyBySku.values()) {
    qtyDueSum += qty;
  }
  return {
    locationCount: locations.size,
    skuCount: qtyBySku.size,
    qtyDueSum,
  };
}

/**
 * Composite job weight for kitting pick labor split:
 * `rawWeight = max(1, locationCount) × max(1, skuCount) × max(1, qtyDueSum)`.
 * Normalized shares sum to 1 across selected jobs; integer quantities use {@link KITTING_PICK_LABOR_ALLOCATION_UNIT_SCALE}.
 */
export function kittingPickLaborRawWeight(metrics: KittingPickLaborJobMetrics): number {
  const locations = Math.max(1, metrics.locationCount);
  const skus = Math.max(1, metrics.skuCount);
  const qty = Math.max(1, metrics.qtyDueSum);
  return locations * skus * qty;
}

/** Splits `totalUnits` across positive weights with largest-remainder allocation. */
export function allocateIntegerLaborWeights(totalUnits: number, rawWeights: readonly number[]): number[] {
  if (rawWeights.length === 0) return [];
  const weightSum = rawWeights.reduce((sum, weight) => sum + Math.max(0, weight), 0);
  if (weightSum <= 0 || totalUnits <= 0) {
    return rawWeights.map(() => 0);
  }
  const base = rawWeights.map((weight) =>
    Math.floor((totalUnits * Math.max(0, weight)) / weightSum),
  );
  let used = base.reduce((sum, value) => sum + value, 0);
  let remainder = totalUnits - used;
  const out = [...base];
  let index = 0;
  while (remainder > 0) {
    out[index % out.length] = (out[index % out.length] ?? 0) + 1;
    remainder -= 1;
    index += 1;
  }
  return out;
}

export function kittingPickLaborTargetForSlot(
  slot: KittingCartSlot,
  pickLines: readonly KittingPickLine[],
): KittingPickLaborAllocationTarget | null {
  if (slot.kind === "transfer_order") {
    if (slot.transferOrderId == null || !Number.isFinite(slot.transferOrderId) || slot.transferOrderId <= 0) {
      return null;
    }
    return { kind: "transfer_order", transferOrderId: Math.trunc(slot.transferOrderId) };
  }

  const jobLine = pickLines.find((line) => line.destinationSlotId === slot.slotKey);
  if (jobLine != null) {
    if (jobLine.job.kind === "batch" && jobLine.job.batchId != null) {
      if (jobLine.job.workOrderId != null && jobLine.job.workOrderId > 0) {
        return { kind: "build_order", buildOrderId: jobLine.job.workOrderId };
      }
      return { kind: "batch", batchId: jobLine.job.batchId };
    }
    if (jobLine.job.workOrderId != null && jobLine.job.workOrderId > 0) {
      return { kind: "build_order", buildOrderId: jobLine.job.workOrderId };
    }
  }

  if (slot.workOrderId != null && Number.isFinite(slot.workOrderId) && slot.workOrderId > 0) {
    return { kind: "build_order", buildOrderId: Math.trunc(slot.workOrderId) };
  }
  if (slot.kind === "batch" && slot.batchIds.length > 0) {
    return { kind: "batch", batchId: slot.batchIds[0]! };
  }
  return null;
}

export function buildKittingPickLaborAllocations(input: {
  pickLines: readonly KittingPickLine[];
  slots: readonly KittingCartSlot[];
}): KittingPickLaborAllocationRow[] {
  const rows: Array<{
    slot: KittingCartSlot;
    metrics: KittingPickLaborJobMetrics;
    rawWeight: number;
    target: KittingPickLaborAllocationTarget;
  }> = [];

  for (const slot of input.slots) {
    const metrics = kittingPickLaborJobMetrics(input.pickLines, slot.slotKey);
    const target = kittingPickLaborTargetForSlot(slot, input.pickLines);
    if (target == null) continue;
    rows.push({
      slot,
      metrics,
      rawWeight: kittingPickLaborRawWeight(metrics),
      target,
    });
  }

  const quantities = allocateIntegerLaborWeights(
    KITTING_PICK_LABOR_ALLOCATION_UNIT_SCALE,
    rows.map((row) => row.rawWeight),
  );

  return rows.map((row, index) => ({
    slotKey: row.slot.slotKey,
    quantity: quantities[index] ?? 0,
    metrics: row.metrics,
    target: row.target,
  }));
}
